import { useState, useEffect } from 'react'

interface SplashPageProps {
  onEnter: () => void
}

export default function SplashPage({ onEnter }: SplashPageProps) {
  const [started, setStarted] = useState(false)
  const [flapOpen, setFlapOpen] = useState(false)
  const [letterSlide, setLetterSlide] = useState(false)

  useEffect(() => {
    const timer1 = setTimeout(() => setFlapOpen(true), 800)
    const timer2 = setTimeout(() => setLetterSlide(true), 1500)
    return () => { clearTimeout(timer1); clearTimeout(timer2) }
  }, [])

  const handleEnter = () => {
    setStarted(true)
    setTimeout(onEnter, 600)
  }

  const handleSkip = () => {
    localStorage.setItem('dear-you-splash-seen', 'true')
    onEnter()
  }

  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-gradient-to-b from-[#3d2b1f] via-[#5a3d2b] to-[#1f150d] overflow-hidden">
      {/* Oil lamp light effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(255,215,0,0.12) 0%, transparent 65%)',
          animation: 'lamp-flicker 5s ease-in-out infinite',
        }}
      />

      {/* Floating dust particles */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 30 }).map((_, idx) => {
          const randomLeft = Math.random() * 100
          const randomTop = Math.random() * 100
          const randomDuration = 4 + idx
          const randomDelay = idx * 0.1
          return (
            <div
              key={`dust-${idx}`}
              className="absolute w-1 h-1 bg-[#ffd700] rounded-full opacity-20"
              style={{
                left: `${randomLeft}%`,
                top: `${randomTop}%`,
                animation: `float-envelope ${randomDuration}s ease-in-out ${randomDelay}s infinite`,
              }}
            />
          )
        })}
      </div>

      {/* Envelope */}
      <div className={`relative transition-all duration-1000 ${started ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
        {/* Envelope body background */}
        <div className="w-64 h-44 bg-[#e8d5c0] rounded-sm shadow-deep relative">
          
          {/* Envelope flap (triangle) */}
          <div
            className="absolute top-0 left-0 right-0 origin-top transition-transform duration-1000 ease-out z-30"
            style={{
              transform: flapOpen ? 'rotateX(180deg)' : 'rotateX(0deg)',
              transformStyle: 'preserve-3d',
              backfaceVisibility: 'visible',
            }}
          >
            <svg viewBox="0 0 256 120" className="w-full h-24 drop-shadow-md" role="img" aria-label="Envelope flap">
              <polygon points="0,0 128,80 256,0" fill="#e8d5c0" stroke="#d4b89a" strokeWidth="1" />
            </svg>
          </div>

          {/* Letter sliding out — z-30 so it rises above the front pocket (z-20) */}
          <div
            className={`absolute inset-x-4 bottom-4 h-36 bg-gradient-to-b from-white to-[#fcfaf2] rounded-sm shadow-sm transition-all duration-1200 ease-out z-30 p-4 border border-sepia-200/50 ${
              letterSlide ? 'translate-y-[-70px] scale-[1.02] rotate-[-2deg]' : 'translate-y-0 opacity-100'
            }`}
          >
            <div className="font-handwriting text-sepia-800 text-lg leading-relaxed border-b border-sepia-100 pb-2">
              见字如晤
            </div>
            <div className="font-serif text-sepia-400 text-[10px] tracking-widest mt-2 uppercase text-center">
              Dear You
            </div>
          </div>

          {/* Envelope front pocket cover (drawn on top of the letter) */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#fdf8f0] via-[#f5e6d3] to-[#e8d5c0] rounded-sm border border-sepia-300/40 z-20 pointer-events-none overflow-hidden shadow-inner">
            {/* Paper noise inside front cover */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.02]" aria-hidden="true">
              <filter id="pocket-noise">
                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch" />
              </filter>
              <rect width="100%" height="100%" filter="url(#pocket-noise)" />
            </svg>
            
            {/* Front pocket diagonal fold lines */}
            <svg viewBox="0 0 256 176" className="absolute inset-0 w-full h-full opacity-[0.12]">
              <polygon points="0,176 128,100 256,176" fill="#e2d2be" stroke="#d4b89a" strokeWidth="1.5" />
              <line x1="0" y1="0" x2="128" y2="100" stroke="#d4b89a" strokeWidth="1.2" />
              <line x1="256" y1="0" x2="128" y2="100" stroke="#d4b89a" strokeWidth="1.2" />
            </svg>
          </div>

          {/* Wax seal */}
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-500 z-40 ${flapOpen ? 'scale-150 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}`}>
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cinnabar to-seal-red-dark flex items-center justify-center shadow-md border border-cinnabar/30">
              <span className="text-white/90 font-serif text-3xl select-none">念</span>
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      <div className={`mt-12 text-center transition-all duration-1000 ${started ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
        <h1 className="text-5xl md:text-6xl font-handwriting text-[#f5e6d3] mb-4 tracking-wide">
          Dear You
        </h1>
        <p className="text-xl text-sepia-300 font-serif italic">
          给阿嬷的情书
        </p>
        <p className="text-sm text-sepia-400 mt-2 font-serif">
          A Letter Wall Inspired by the Movie
        </p>
      </div>

      {/* CTA */}
      <div className={`mt-10 transition-all duration-1000 ${started ? 'opacity-0' : 'opacity-100'}`}>
        <button
          type="button"
          onClick={handleEnter}
          className="px-10 py-4 bg-[#f5e6d3] text-[#3d2b1f] font-handwriting text-xl rounded-lg shadow-lg hover:bg-[#e8d5c0] transition-colors"
        >
          {localStorage.getItem('dear-you-splash-seen')
            ? 'Enter the Wall'
            : 'Enter the Wall'}
        </button>
      </div>

      {/* Skip */}
      <button
        type="button"
        onClick={handleSkip}
        className="mt-6 text-sepia-500 hover:text-sepia-300 text-sm font-serif transition-colors"
      >
        Skip
      </button>
    </div>
  )
}
